// Who offers what on a right-click. Two ways to add entries, both without touching the generated templates:
// - an element provider (`setContextMenu(el, fn)` / `useContextMenu(ref, fn)`): used for everything inside that
//   element (PDF preview, PDF viewer, …). Nested elements: the innermost provider comes first.
// - a global provider (`registerContextMenu(fn)`): asked for every right-click with the clicked element, e.g.
//   RimoCompta's provider that reads `data-ctx="doc"` rows (attributes added by its compiler patches).
// Each provider returns a group of entries (or nothing); the host joins the groups with separators.
import { useEffect, useRef, type RefObject } from 'react';
import type { CtxEntry } from './menu';

export interface CtxEvent { target: Element; x: number; y: number; /** Text selected when the menu opened. */ selection: string }
export type CtxProvider = (ev: CtxEvent) => Array<CtxEntry | null | undefined | false> | null | undefined | void;

const byEl = new WeakMap<Element, CtxProvider>();
const globals: Array<{ p: CtxProvider; order: number }> = [];

/** Element provider; returns the remover. */
export function setContextMenu(el: Element, p: CtxProvider): () => void {
  byEl.set(el, p);
  return () => { if (byEl.get(el) === p) byEl.delete(el); };
}

/** React: element provider for `ref` while the component is mounted (the latest `p` is always used). */
export function useContextMenu(ref: RefObject<Element | null>, p: CtxProvider): void {
  const latest = useLatest(p);
  // No deps: the element may appear later (conditional rendering); re-registering is a WeakMap set.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    return setContextMenu(el, (ev) => latest.current(ev));
  });
}
function useLatest<T>(v: T) {
  const r = useRef(v);
  r.current = v;
  return r;
}

/** Global provider; lower `order` comes first (element providers always come before globals). */
export function registerContextMenu(p: CtxProvider, order = 50): () => void {
  const it = { p, order };
  globals.push(it);
  globals.sort((a, b) => a.order - b.order);
  return () => { const i = globals.indexOf(it); if (i >= 0) globals.splice(i, 1); };
}

/** All groups for a right-click: element providers from the clicked element outwards, then the globals. */
export function collect(ev: CtxEvent): CtxEntry[][] {
  const out: CtxEntry[][] = [];
  const push = (p: CtxProvider) => {
    try {
      const g = p(ev);
      if (g && g.length) out.push(g.filter(Boolean) as CtxEntry[]);
    } catch (e) {
      console.error('context menu provider failed', e);
    }
  };
  for (let el: Element | null = ev.target; el; el = el.parentElement) { const p = byEl.get(el); if (p) push(p); }
  for (const g of [...globals]) push(g.p);
  return out;
}

// ---------- big view opened from a menu (rendered by the host when the program passes `renderView`) ----------
export const VIEW_EVENT = 'rimon:ctx-view';
/** `ref` = the program's id of what is shown (RimoCompta: the document, for its notes layer – shared/overlay). */
export interface ViewRequest { url: string; title?: string; ref?: string }
export const openBigView = (r: ViewRequest) => window.dispatchEvent(new CustomEvent<ViewRequest>(VIEW_EVENT, { detail: r }));
